/* Padelplay: Trustpilot reviews carousel (sections/pp-trustpilot-reviews.liquid).
   Moves one card every N s, pauses on hover/focus/touch/drag, mouse-drag to scroll,
   loads the official TrustBox script if widget code was pasted. */
(() => {
  if (customElements.get('pp-tp-reviews')) return;

  const TP_SCRIPT = 'https://widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js';
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function loadTrustBoxes(root) {
    const widgets = root.querySelectorAll('.trustpilot-widget');
    if (!widgets.length) return;
    const run = () => widgets.forEach((w) => window.Trustpilot && window.Trustpilot.loadFromElement(w, true));
    if (window.Trustpilot) return run();
    let s = document.querySelector('script[src*="tp.widget.bootstrap"]');
    if (!s) {
      s = document.createElement('script');
      s.src = TP_SCRIPT;
      s.async = true;
      document.head.appendChild(s);
    }
    s.addEventListener('load', run, { once: true });
  }

  class PpTpReviews extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.pp-tp__track');
      if (!this.track) return;
      this.delay = (parseInt(this.dataset.autoplay, 10) || 0) * 1000;
      this.paused = new Set();
      this.visible = false;
      this.ac = new AbortController();
      const opt = { signal: this.ac.signal };

      loadTrustBoxes(this);

      // pause reasons
      const hold = (why) => () => { this.paused.add(why); };
      const free = (why) => () => { this.paused.delete(why); };
      this.track.addEventListener('mouseenter', hold('hover'), opt);
      this.track.addEventListener('mouseleave', free('hover'), opt);
      this.addEventListener('focusin', hold('focus'), opt);
      this.addEventListener('focusout', free('focus'), opt);
      this.track.addEventListener('touchstart', () => {
        this.paused.add('touch');
        clearTimeout(this.touchTimer);
      }, { ...opt, passive: true });
      this.track.addEventListener('touchend', () => {
        clearTimeout(this.touchTimer);
        this.touchTimer = setTimeout(() => this.paused.delete('touch'), 6000);
      }, { ...opt, passive: true });

      this.track.addEventListener('scroll', () => this.updateEnd(), { ...opt, passive: true });
      this.setupDrag(opt);
      this.updateEnd();

      // only tick while on screen
      this.io = new IntersectionObserver(([e]) => { this.visible = e.isIntersecting; }, { threshold: 0.3 });
      this.io.observe(this);

      if (this.delay > 0) this.timer = setInterval(() => this.tick(), this.delay);

      // theme editor: show the selected review
      document.addEventListener('shopify:block:select', (e) => {
        if (!this.contains(e.target)) return;
        this.paused.add('editor');
        this.track.scrollTo({ left: e.target.offsetLeft - this.track.offsetLeft, behavior: 'smooth' });
      }, opt);
      document.addEventListener('shopify:block:deselect', (e) => {
        if (this.contains(e.target)) this.paused.delete('editor');
      }, opt);
    }

    disconnectedCallback() {
      clearInterval(this.timer);
      clearTimeout(this.touchTimer);
      if (this.io) this.io.disconnect();
      if (this.ac) this.ac.abort();
    }

    step() {
      const card = this.track.querySelector('.pp-tp-card');
      if (!card) return 0;
      return card.getBoundingClientRect().width + (parseFloat(getComputedStyle(this.track).columnGap) || 0);
    }

    atEnd() {
      return this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 4;
    }

    updateEnd() {
      this.classList.toggle('is-at-end', this.atEnd());
    }

    tick() {
      if (this.paused.size || !this.visible || document.hidden || reduceMotion.matches) return;
      if (this.track.scrollWidth <= this.track.clientWidth + 4) return; // nothing to scroll
      if (this.atEnd()) this.track.scrollTo({ left: 0 });
      else this.track.scrollBy({ left: this.step() });
    }

    setupDrag(opt) {
      let startX = 0;
      let startLeft = 0;
      let moved = 0;
      let down = false;
      const t = this.track;

      t.addEventListener('pointerdown', (e) => {
        if (e.pointerType !== 'mouse' || e.button !== 0) return;
        down = true;
        moved = 0;
        startX = e.clientX;
        startLeft = t.scrollLeft;
        this.paused.add('drag');
      }, opt);

      window.addEventListener('pointermove', (e) => {
        if (!down) return;
        const dx = e.clientX - startX;
        moved = Math.max(moved, Math.abs(dx));
        if (moved > 5) {
          this.classList.add('is-dragging');
          t.scrollLeft = startLeft - dx;
        }
      }, opt);

      const end = () => {
        if (!down) return;
        down = false;
        this.paused.delete('drag');
        if (!this.classList.contains('is-dragging')) return;
        // snap to the nearest card after a drag
        const step = this.step();
        const left = t.scrollLeft;
        this.classList.remove('is-dragging');
        if (step) {
          t.scrollLeft = left;
          t.scrollTo({ left: Math.round(left / step) * step });
        }
      };
      window.addEventListener('pointerup', end, opt);
      window.addEventListener('pointercancel', end, opt);

      // a drag must not open a review link
      t.addEventListener('click', (e) => {
        if (moved > 5) {
          e.preventDefault();
          e.stopPropagation();
          moved = 0;
        }
      }, { ...opt, capture: true });
      t.addEventListener('dragstart', (e) => e.preventDefault(), opt);
    }
  }

  customElements.define('pp-tp-reviews', PpTpReviews);
})();
